import { TabContext, TabPanel } from '@mui/lab';
import { alpha, Box, Button, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useLocation, useNavigate } from 'react-router';

import { useTabs, useUserAccessStore, useUserDetailsStore } from '@zeta-core';
import { CalendarIcon } from '@zeta-core/assets';
import { CLIENT_TYPES, FactFindType, IClientCardItem, IFactFindEntity } from '@zeta-types';
import { RouterLinks } from '../../../../lib/advisor-route-paths';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import BusinessOwnership from './business-ownership';
import { FinancialSnapshot } from './financial-snapshot';
import Summary from './summary';

dayjs.extend(advancedFormat);

export const InitialMeetingSummary = ({ isClient, isManagedClient, from }: { isClient?: boolean; isManagedClient?: boolean; from: FactFindType }) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const theme = useTheme();
  const location = useLocation();
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = {
      clientData: {
        clientID: accessibleEntityID,
      },
    };
  }
  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;
  const navigate = useNavigate();

  const TABS = {
    SUMMARY: { label: 'Summary', value: 'SUMMARY', show: true },
    FINANCIAL_SNAPSHOT: { label: 'Financial Snapshot', value: 'FINANCIAL_SNAPSHOT', show: state?.clientData?.clientType === CLIENT_TYPES.PERSON || isClient },
    BUSINESS_OWNERSHIP: { label: 'Business Ownership', value: 'BUSINESS_OWNERSHIP', show: state?.clientData?.clientType === CLIENT_TYPES.BUSINESS },
  };
  const isReadOnly = from === FactFindType.PARA_PLANNER || isClient || readOnly;

  const tab = useTabs(from === FactFindType.FACT_FIND ? TABS.SUMMARY.value : TABS.FINANCIAL_SNAPSHOT.value);

  return (
    <Box>
      <Stack spacing={5}>
        <Stack spacing={3}>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" id="overview-title">
                Overview
              </Typography>
              {isClient || isReadOnly ? null : (
                <Button
                  startIcon={<CalendarIcon />}
                  id="clientInteractionHubButton"
                  variant="soft"
                  color="primary"
                  onClick={() =>
                    navigate(isManagedClient ? RouterLinks.managedClientInteractionHub : RouterLinks.factFindClientInteractionHub, { state: state })
                  }
                >
                  Client Interaction Hub
                </Button>
              )}
            </Stack>

            <TabContext value={tab.value}>
              <Tabs {...tab} sx={{ borderBottom: 2, borderColor: alpha(theme.palette.grey[500], 0.08) }}>
                {TABS.SUMMARY.show && (
                  <Tab label={TABS.SUMMARY.label} id={TABS.SUMMARY.label} value={TABS.SUMMARY.value} sx={{ marginRight: '20px !important' }} />
                )}
                {TABS.FINANCIAL_SNAPSHOT.show && (
                  <Tab label={TABS.FINANCIAL_SNAPSHOT.label} id={TABS.FINANCIAL_SNAPSHOT.label} value={TABS.FINANCIAL_SNAPSHOT.value} />
                )}
                {TABS.BUSINESS_OWNERSHIP.show && (
                  <Tab label={TABS.BUSINESS_OWNERSHIP.label} id={TABS.BUSINESS_OWNERSHIP.label} value={TABS.BUSINESS_OWNERSHIP.value} />
                )}
              </Tabs>

              <TabPanel value={TABS.SUMMARY.value} id={TABS.SUMMARY.value} sx={{ p: 0 }}>
                <Summary clientID={isClient ? accessibleEntityID : state?.clientData?.clientID} />
              </TabPanel>

              <TabPanel value={TABS.FINANCIAL_SNAPSHOT.value} id={TABS.FINANCIAL_SNAPSHOT.value} sx={{ p: 0 }}>
                <FinancialSnapshot clientID={isClient ? accessibleEntityID : state?.clientData?.clientID} />
              </TabPanel>

              <TabPanel value={TABS.BUSINESS_OWNERSHIP.value} id={TABS.BUSINESS_OWNERSHIP.value} sx={{ p: 0 }}>
                <BusinessOwnership />
              </TabPanel>
            </TabContext>
          </Stack>
        </Stack>
      </Stack>
      {isClient || isReadOnly ? null : <FactFindFooter hideSave from={from} isManagedClient={isManagedClient} />}
    </Box>
  );
};
